import { DocumentData } from 'firebase/firestore';
import Fuse, { FuseResult, IFuseOptions } from 'fuse.js';

export function searchWithFuse<T, R>(
  allData: T[],
  options: IFuseOptions<T>,
  searchValue: string,
  mapper: (result: FuseResult<T>) => R,
): R[] {
  const fuse = new Fuse(allData, options);
  const searchResults = fuse.search(searchValue);

  return searchResults
    .sort((a, b) => (a.score ?? 0) - (b.score ?? 0))
    .slice(0, 10)
    .map(mapper);
}

export const fileNameSearch = (results: DocumentData[], searchTerm: string): DocumentData[] => {
  const options = {
    keys: ['input.original_files.file_name'],
    includeScore: true,
    threshold: 0.6,
  };

  const fuse = new Fuse<DocumentData>(results, options);

  const result = fuse.search(searchTerm).map(result => result.item);

  return result;
};

// Pattern用のフリーワード検索（search_textフィールドで部分一致＋あいまい検索）
export const patternFreeWordSearch = (
  results: DocumentData[],
  searchTerm: string,
): DocumentData[] => {
  const keyword = searchTerm.toLowerCase();

  // 1. 完全一致（部分一致）を抽出
  const exactMatches = results.filter(item => {
    if (!item.search_text) return false;
    return item.search_text.toLowerCase().includes(keyword);
  });

  // 2. あいまい検索で残りを抽出
  const fuse = new Fuse<DocumentData>(results, {
    keys: ['search_text'],
    threshold: 0.4,
    includeScore: true,
    ignoreLocation: true,
    minMatchCharLength: 2,
  });

  const fuzzyResults = fuse
    .search(searchTerm)
    .map(result => result.item)
    .filter(item => !exactMatches.some(exact => exact.id === item.id));

  // 3. 完全一致を先頭に、あいまい検索結果を後ろに配置
  return [...exactMatches, ...fuzzyResults];
};

// 単一値に対するあいまい一致判定（OCR項目検索用）
export const fuzzyMatch = (value: string, searchTerm: string): boolean => {
  const fuse = new Fuse([{ value }], {
    keys: ['value'],
    threshold: 0.4,
    ignoreLocation: true,
    minMatchCharLength: 2,
  });

  return fuse.search(searchTerm).length > 0;
};

// Pattern用のファイル名検索（original_filesからfile_nameを検索）
export const patternFileNameSearch = <T extends DocumentData = DocumentData>(
  results: T[],
  searchTerm: string,
): T[] => {
  const options = {
    keys: ['original_files.file_name'],
    includeScore: true,
    threshold: 0.4,
  };

  const fuse = new Fuse<T>(results, options);

  const result = fuse.search(searchTerm).map(result => result.item);

  return result;
};
